import React from 'react';

const Statistics = ({ stats, channels, content }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAnalysisStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'text-green-600 bg-green-100';
      case 'analyzing': return 'text-yellow-600 bg-yellow-100';
      case 'pending': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 1: return 'text-red-600 bg-red-100';
      case 2: return 'text-orange-600 bg-orange-100';
      case 3: return 'text-yellow-600 bg-yellow-100';
      case 4: return 'text-blue-600 bg-blue-100';
      case 5: return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Monitoring Statistics</h2>
      
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{stats.totalChannels || 0}</div>
          <div className="text-sm text-gray-600">Total Channels</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-indigo-600">{stats.totalWebsites || 0}</div>
          <div className="text-sm text-gray-600">Total Websites</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{stats.totalContent || 0}</div>
          <div className="text-sm text-gray-600">Total Content</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-orange-600">{stats.pendingAnalysis || 0}</div>
          <div className="text-sm text-gray-600">Pending Analysis</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel Performance */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Channel Performance</h3>
          <div className="space-y-3">
            {channels.length === 0 ? (
              <p className="text-gray-500 text-sm">No channels added yet</p>
            ) : (
              channels.map(channel => (
                <div key={channel.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{channel.name}</div>
                      <div className="text-sm text-gray-500">
                        {channel.channel_id ? `@${channel.channel_id}` : 'ID not set'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(channel.is_active ? 'active' : 'inactive')}`}>
                      {channel.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getImportanceColor(channel.importance || 3)}`}>
                      {channel.importance === 1 ? 'Critical' : 
                       channel.importance === 2 ? 'High' : 
                       channel.importance === 3 ? 'Medium' : 
                       channel.importance === 4 ? 'Low' : 'Minimal'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Content Analysis Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Content Analysis Status</h3>
          <div className="space-y-3">
            {content.length === 0 ? (
              <p className="text-gray-500 text-sm">No content available</p>
            ) : (
              <>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900">Analyzed</span>
                  <span className="text-lg font-bold text-green-600">
                    {content.filter(c => c.analysis_status === 'analyzed').length}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900">Analyzing</span>
                  <span className="text-lg font-bold text-yellow-600">
                    {content.filter(c => c.analysis_status === 'analyzing').length}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900">Pending</span>
                  <span className="text-lg font-bold text-blue-600">
                    {content.filter(c => c.analysis_status === 'pending').length}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900">Failed</span>
                  <span className="text-lg font-bold text-red-600">
                    {content.filter(c => c.analysis_status === 'failed').length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {content.length === 0 ? (
            <p className="text-gray-500 text-sm">No recent activity</p>
          ) : (
            content.slice(0, 5).map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.channel}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${getAnalysisStatusColor(item.analysis_status)}`}>
                    {item.analysis_status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(item.published_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-900">Backend API</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Connected and responding</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-900">Content Monitoring</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Active and monitoring</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-900">Last Check</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {stats.lastCheck ? new Date(stats.lastCheck).toLocaleString() : 'Never'}
            </p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-900">Total Sources</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {(stats.totalChannels || 0) + (stats.totalWebsites || 0)} sources
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
